'use client';

import { ArrowIcon } from '@ngg/icons';
import { useInView } from 'framer-motion';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@ngg/storefront-utils';
import type { Image as ImageType } from '@ts/image';
import Image from '@ui/components/Image/Image';

const IMAGE_WIDTH = 65;
const IMAGE_HEIGHT = 75;
const GAP_SIZE = 8;

const isFullyVisible = (el: HTMLElement, container: HTMLElement) => {
  const { bottom, top, left, right } = el?.getBoundingClientRect() || {};
  const containerRect = container?.getBoundingClientRect() || {};

  return (
    left >= containerRect.left &&
    right <= containerRect.right &&
    top >= containerRect.top &&
    bottom <= containerRect.bottom
  );
};

type ArrowButtonProps = { isInView: boolean; onClick: () => void };

const ArrowButtonStart = ({ isInView, onClick }: ArrowButtonProps) => (
  <div
    className={cn(
      'h-13 w-13 pointer-events-none absolute left-0 z-10 hidden bg-gradient-to-r from-white transition-opacity md:flex lg:bg-gradient-to-b',
      isInView ? 'opacity-0' : 'opacity-100',
    )}>
    <button
      onClick={onClick}
      type="button"
      className="pointer-events-auto absolute top-[24px] z-10 flex h-6 w-6 select-none items-center justify-center rounded-full bg-black text-lg lg:left-[21px] lg:top-0">
      <ArrowIcon className="rotate-180 text-white lg:-rotate-90" />
    </button>
  </div>
);

const ArrowButtonEnd = ({ isInView, onClick }: ArrowButtonProps) => (
  <div
    className={cn(
      'h-13 w-13 pointer-events-none absolute right-0 z-10 hidden bg-gradient-to-l from-white transition-opacity md:flex lg:bottom-0 lg:left-0 lg:right-auto lg:bg-gradient-to-t',
      isInView ? 'opacity-0' : 'opacity-100',
    )}>
    <button
      onClick={onClick}
      type="button"
      className="pointer-events-auto absolute right-0 top-[24px] z-10 flex h-6 w-6 select-none items-center justify-center rounded-full bg-black text-lg lg:bottom-0 lg:left-[21px] lg:top-auto">
      <ArrowIcon className="text-white lg:rotate-90" />
    </button>
  </div>
);

export default function Thumbnails({
  goToItem,
  activeIndex,
  images,
  className,
}: {
  goToItem: (index: number, smooth?: boolean) => void;
  activeIndex: number;
  images: ImageType[];
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const anchorRefStart = useRef<HTMLDivElement>(null);
  const anchorRefEnd = useRef<HTMLDivElement>(null);
  const isInViewStart = useInView(anchorRefStart);
  const isInViewEnd = useInView(anchorRefEnd);
  const [mounted, setMounted] = useState(false);

  const onArrowClick = (direction: 'start' | 'end') => {
    const currentScrollValue = Number(
      ref.current?.scrollTop || ref.current?.scrollLeft,
    );

    const directionValue =
      direction === 'start'
        ? -(IMAGE_HEIGHT + GAP_SIZE)
        : IMAGE_HEIGHT + GAP_SIZE;
    const targetScrollValue = currentScrollValue + directionValue;

    ref.current?.scrollTo({
      top: targetScrollValue,
      left: targetScrollValue,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!ref.current) return;

    setMounted(true);

    const currentChild = ref.current?.children[activeIndex] as HTMLDivElement;

    if (isFullyVisible(currentChild, ref.current as HTMLDivElement)) return;

    ref?.current?.scrollTo({
      top: currentChild?.offsetTop,
      left: currentChild?.offsetLeft,
      behavior: 'smooth',
    });
  }, [activeIndex]);

  return (
    <div className="flex">
      {mounted && (
        <ArrowButtonStart
          isInView={isInViewStart}
          onClick={() => onArrowClick('start')}
        />
      )}
      <div ref={ref} className={cn('flex gap-2 scrollbar-none', className)}>
        <div ref={anchorRefStart} />
        {images.map((media, i) => (
          <div
            key={`${media.id}-${String(i)}`}
            className={cn(
              'h-[75px] w-[65px] flex-shrink-0 bg-beige',
              i === activeIndex && 'ring-1 ring-inset ring-black',
            )}
            role="button"
            tabIndex={0}
            onClick={() => goToItem(i, false)}>
            {media.type === 'image' ? (
              <Image
                src={media.src}
                alt={media.alt}
                width={IMAGE_WIDTH}
                height={IMAGE_HEIGHT}
                className={cn('h-full w-full object-cover mix-blend-multiply')}
                sizes="128px"
              />
            ) : null}
          </div>
        ))}
        <div ref={anchorRefEnd} />
      </div>
      {mounted && (
        <ArrowButtonEnd
          isInView={isInViewEnd}
          onClick={() => onArrowClick('end')}
        />
      )}
    </div>
  );
}
